import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { trackEvent } from '../lib/metaPixel'

/**
 * ActivarCuenta — Paso 2 del signup magic-link (14-jul-2026).
 *
 * Flow:
 *  1. Login.jsx (modo signup) hace supabase.auth.signInWithOtp() con
 *     emailRedirectTo = https://panel.limpack.uy/activar-cuenta?next=...
 *  2. Al user le llega mail "Activá tu cuenta" con link firmado por Supabase.
 *  3. Al clickearlo, Supabase valida el token y crea la session.
 *     El navegador cae acá con la session ya lista.
 *  4. Este componente pide contraseña + confirmación y hace updateUser({ password }).
 *  5. Redirige al `next` que venía en el signup (o /cliente por default).
 *
 * NO reutiliza SetPassword.jsx a propósito — SetPassword es para RESET
 * (usuarios existentes olvidan contraseña) y su copy/tono es distinto.
 * Acá el user está *activando* su cuenta por primera vez y necesita
 * transmisión visual de seguridad ("elegí tu contraseña" con candado,
 * checklist de requisitos, etc.).
 */
export default function ActivarCuenta() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const rawNext = params.get('next')
  const next = (rawNext && typeof rawNext === 'string' && rawNext.startsWith('/') && !rawNext.startsWith('//'))
    ? rawNext
    : '/cliente'

  const [ready, setReady] = useState(false)  // sesión detectada
  const [checking, setChecking] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  // Al montar: Supabase ya debería tener la session (el magic-link la creó).
  // Verificamos y sacamos el email para mostrarlo.
  useEffect(() => {
    let cancel = false
    ;(async () => {
      // Damos un pequeño margen para que Supabase termine de procesar el hash del URL.
      await new Promise(r => setTimeout(r, 300))
      const { data } = await supabase.auth.getSession()
      if (cancel) return
      if (data?.session) {
        setReady(true)
        setEmail(data.session.user?.email || '')
      } else {
        setError('El link expiró o ya fue usado. Pedí uno nuevo desde el signup.')
      }
      setChecking(false)
    })()
    return () => { cancel = true }
  }, [])

  // Requisitos de password (mostramos check en vivo para transmitir seguridad)
  const pwLongEnough = password.length >= 8
  const pwHasLetter = /[a-zA-Z]/.test(password)
  const pwHasNumber = /\d/.test(password)
  const pwMatch = password.length > 0 && password === password2
  const pwValid = pwLongEnough && pwHasLetter && pwHasNumber && pwMatch

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!pwLongEnough) { setError('La contraseña debe tener al menos 8 caracteres.'); return }
    if (!pwHasLetter || !pwHasNumber) { setError('Combiná letras y números.'); return }
    if (password !== password2) { setError('Las contraseñas no coinciden.'); return }
    setLoading(true)
    const { error: upErr } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (upErr) { setError(upErr.message); return }
    // Meta Pixel: CompleteRegistration = flow signup 100% terminado (con password activa)
    trackEvent('CompleteRegistration', { content_name: 'password_creada', status: 'success' })
    setDone(true)
    // Pequeña pausa para que el user vea el "listo" y confíe en que se guardó.
    setTimeout(() => navigate(next, { replace: true }), 1500)
  }

  return (
    <div className="lp-act">
      <nav className="lp-act-nav">
        <div className="lp-act-nav-inner">
          <a href="https://limpack.uy" className="lp-act-logo">Lim<span>pack</span></a>
          <span className="lp-act-nav-step">Paso 2 de 2 · Elegí tu contraseña</span>
        </div>
      </nav>

      <main className="lp-act-main">
        <div className="lp-act-card">
          {/* Icono candado (transmite seguridad al llegar del mail) */}
          <div className="lp-act-lock">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="10" width="16" height="10" rx="2" stroke="#00A8B5" strokeWidth="2"/>
              <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#00A8B5" strokeWidth="2" fill="none"/>
              <circle cx="12" cy="15" r="1.5" fill="#00A8B5"/>
            </svg>
          </div>

          {done ? (
            <>
              <div className="lp-act-head">
                <h1>Cuenta activada</h1>
                <p>Todo listo. Te llevamos a tu panel…</p>
              </div>
              <div className="lp-act-ok">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#10B981"/>
                  <path d="M7 12l3.5 3.5L17 9" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
            </>
          ) : checking ? (
            <div className="lp-act-head">
              <h1>Verificando link…</h1>
              <p>Un segundo.</p>
            </div>
          ) : !ready ? (
            <>
              <div className="lp-act-head">
                <h1>Link no válido</h1>
                <p>{error}</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/suscribirme')}
                className="lp-act-btn"
              >
                Volver a empezar
              </button>
            </>
          ) : (
            <>
              <div className="lp-act-head">
                <h1>Elegí tu contraseña</h1>
                <p>Vas a usarla para entrar rápido las próximas veces{email ? <> a <strong>{email}</strong></> : null}.</p>
              </div>

              <form onSubmit={handleSubmit} className="lp-act-form">
                <label className="lp-act-field">
                  <b>Contraseña</b>
                  <div className="lp-act-pw-wrap">
                    <input
                      type={showPw ? 'text' : 'password'}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(s => !s)}
                      className="lp-act-pw-toggle"
                      aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPw ? '🙈' : '👁'}
                    </button>
                  </div>
                </label>

                <label className="lp-act-field">
                  <b>Confirmá tu contraseña</b>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password2}
                    onChange={e => setPassword2(e.target.value)}
                    placeholder="Repetí la contraseña"
                  />
                </label>

                {/* Checklist en vivo — muestra seguridad + guía al user */}
                <ul className="lp-act-req">
                  <li className={pwLongEnough ? 'ok' : ''}>
                    <span className="lp-act-req-dot">{pwLongEnough ? '✓' : '·'}</span>
                    Al menos 8 caracteres
                  </li>
                  <li className={pwHasLetter && pwHasNumber ? 'ok' : ''}>
                    <span className="lp-act-req-dot">{pwHasLetter && pwHasNumber ? '✓' : '·'}</span>
                    Combina letras y números
                  </li>
                  <li className={pwMatch ? 'ok' : ''}>
                    <span className="lp-act-req-dot">{pwMatch ? '✓' : '·'}</span>
                    Coinciden las dos veces
                  </li>
                </ul>

                {error && <div className="lp-act-err">{error}</div>}

                <button
                  type="submit"
                  disabled={loading || !pwValid}
                  className="lp-act-btn"
                >
                  {loading ? 'Guardando…' : 'Activar mi cuenta'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="lp-act-help">
          ¿Necesitás ayuda? Escribinos al{' '}
          <a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp 098 610 001</a>
        </p>
      </main>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00A8B5;
  --line2: #e2e8f0;
}
html, body { margin: 0; background: var(--navy) }
.lp-act {
  min-height: 100vh;
  position: relative;
  background: linear-gradient(135deg, #0d1f3d 0%, #1a3461 45%, #1a5cb8 100%);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
  overflow: hidden;
}
.lp-act::before, .lp-act::after {
  content: ''; position: absolute; pointer-events: none;
  border-radius: 50%; filter: blur(80px);
}
.lp-act::before {
  width: 420px; height: 420px;
  top: -120px; right: -120px;
  background: radial-gradient(circle, rgba(0,196,212,.45) 0%, rgba(0,196,212,0) 70%);
}
.lp-act::after {
  width: 380px; height: 380px;
  bottom: -100px; left: -120px;
  background: radial-gradient(circle, rgba(26,92,184,.55) 0%, rgba(26,92,184,0) 70%);
}
.lp-act-nav {
  position: relative; z-index: 2;
  background: rgba(13,31,61,.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.lp-act-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 16px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.lp-act-logo {
  font-size: 22px; font-weight: 900;
  color: #fff; text-decoration: none;
  letter-spacing: -.02em;
}
.lp-act-logo span { color: var(--teal) }
.lp-act-nav-step {
  font-size: 12px; color: rgba(255,255,255,.75);
  background: rgba(0,168,181,.18);
  border: 1px solid rgba(0,168,181,.35);
  padding: 6px 12px; border-radius: 999px;
  font-weight: 600;
}
.lp-act-main {
  position: relative; z-index: 2;
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 16px;
  gap: 18px;
}
.lp-act-card {
  background: #fff;
  width: 100%; max-width: 420px;
  border-radius: 20px;
  padding: 36px 28px;
  box-shadow: 0 8px 40px rgba(26,52,97,.10);
  box-sizing: border-box;
}
@media (min-width: 480px) {
  .lp-act-card { padding: 40px 36px }
}
.lp-act-lock {
  width: 60px; height: 60px;
  margin: 0 auto 18px;
  display: flex; align-items: center; justify-content: center;
  background: #ecfeff;
  border: 1.5px solid #a5f3fc;
  border-radius: 18px;
}
.lp-act-head { text-align: center; margin-bottom: 22px }
.lp-act-head h1 {
  font-size: 22px; font-weight: 900;
  color: var(--navy); margin: 0 0 6px;
  letter-spacing: -.01em;
}
.lp-act-head p {
  font-size: 13px; color: #64748b; margin: 0;
  line-height: 1.55;
}
.lp-act-ok {
  display: flex; justify-content: center;
  padding: 20px 0;
}
.lp-act-form { display: flex; flex-direction: column; gap: 14px }
.lp-act-field { display: block }
.lp-act-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em;
  text-transform: uppercase; margin-bottom: 6px;
}
.lp-act-field input {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 15px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.lp-act-field input:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}
.lp-act-pw-wrap { position: relative }
.lp-act-pw-wrap input { padding-right: 44px }
.lp-act-pw-toggle {
  position: absolute; right: 8px; top: 50%;
  transform: translateY(-50%);
  background: none; border: 0;
  padding: 6px 8px; font-size: 16px;
  cursor: pointer; color: #94a3b8;
}
.lp-act-pw-toggle:hover { color: var(--blue) }
.lp-act-req {
  list-style: none;
  margin: 4px 0 0;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid var(--line2);
  border-radius: 10px;
  display: flex; flex-direction: column; gap: 6px;
}
.lp-act-req li {
  font-size: 12px;
  color: #64748b;
  display: flex; align-items: center; gap: 8px;
  transition: color .15s;
}
.lp-act-req li.ok { color: #059669; font-weight: 600 }
.lp-act-req-dot {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #e2e8f0; color: #94a3b8;
  font-size: 11px; font-weight: 700; line-height: 1;
  transition: background .15s, color .15s;
}
.lp-act-req li.ok .lp-act-req-dot {
  background: #10b981; color: #fff;
}
.lp-act-err {
  background: #fef2f2; border: 1px solid #fca5a5;
  color: #dc2626; border-radius: 10px;
  padding: 10px 14px; font-size: 13px;
}
.lp-act-btn {
  width: 100%;
  background: linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%);
  color: #fff; font-weight: 700; font-size: 15px;
  padding: 14px 20px; border: 0; border-radius: 10px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(0,196,212,.25);
  transition: transform .12s, box-shadow .12s, opacity .15s;
  margin-top: 6px;
}
.lp-act-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,196,212,.35);
}
.lp-act-btn:disabled { opacity: .55; cursor: not-allowed }
.lp-act-help {
  font-size: 12px; color: rgba(255,255,255,.55);
  text-align: center; margin: 0;
}
.lp-act-help a {
  color: var(--teal); text-decoration: none; font-weight: 600;
}
.lp-act-help a:hover { color: #fff; text-decoration: underline }
`
